import type { CompanyIdNameParentIdDto, ConfigInfoViewModel, LoginAnswer, ModuleRights } from "@vintecc/capture-api-types/v1";
import { create } from "zustand";
import { ENV } from "@/env";
import { isAxiosError } from "axios";

import { ParsedLocation, UseNavigateResult } from "@tanstack/react-router";
import { CAPTURE_ROLES } from "@/constants";
import { apiDefaultParams } from "@/api/default.params";
import { getTokenExpiry, registerSwCallbacks, scheduleRefresh } from "@/sw-register";

import userApi from "@/api/user.api";
import companyApi from "@/api/company.api";
import { registerCompanyViewGetter } from "@/api/configuration.api";

interface AppState {
  loginAnswer: LoginAnswer | null;
  isAdmin: boolean;
  isLoading: boolean;
  isSwitchingCompanyView: boolean;
  error: string | null;
  currentCompany: string;
  companyList: CompanyIdNameParentIdDto[];
  currentTitle: string;
  configInfo: ConfigInfoViewModel;
  hasAppAccess: (appName: string) => boolean;
  getAppModule: (appName: string, moduleName: string) => ModuleRights | undefined;
  refreshMe: () => Promise<void>;
  refreshLoginAnswer: () => Promise<void>;
  loadLoginData: () => Promise<void>;
  switchCompany: (company: string, navigate: UseNavigateResult<string>, routerLocation: ParsedLocation) => Promise<void>;
  setCurrentTitle: (title: string) => void;
  setCurrentCompany: (currentCompany: string) => void;
  setLoginAnswer: (loginAnswer: LoginAnswer) => void;
  setIsSwitchingCompanyView: (isSwitchingCompanyView: boolean) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  loginAnswer: null,
  isLoading: true,
  error: null,
  currentCompany: "",
  companyList: [],
  currentTitle: "",
  isSwitchingCompanyView: false,
  configInfo: {},
  isAdmin: false,
  setIsSwitchingCompanyView: (isSwitchingCompanyView) => {
    set({ isSwitchingCompanyView });
  },
  hasAppAccess: (appName: string) => {
    const { loginAnswer } = get();
    return loginAnswer?.rights?.find((right) => right.app === appName)?.modules?.some((module) => module.view) ?? false;
  },
  getAppModule: (appName: string, moduleName: string) => {
    const { loginAnswer } = get();
    const app = loginAnswer?.rights?.find(({ app }) => app === appName);
    return app?.modules?.find(({ module }) => module === moduleName);
  },
  // Action to load login data
  loadLoginData: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await userApi.me(apiDefaultParams.apiVersion);
      const companies = await companyApi.v1.getAllCompaniesNameId(apiDefaultParams.authorization, apiDefaultParams.authVersion, "");
      const configInfo = await companyApi.v1.getConfigInfo(
        apiDefaultParams.authorization,
        apiDefaultParams.authVersion,
        apiDefaultParams.companyView,
      );

      set({
        loginAnswer: response.data,
        isAdmin: response.data.captureUser?.role === CAPTURE_ROLES.ADMIN,
        isLoading: false,
        currentCompany: response.data.companyView!,
        companyList: companies?.data ?? [],
        configInfo: configInfo.data,
      });

      const companyId = companies.data?.find((c) => c.name === response.data.captureUser?.company)?.id;
      window.localStorage.setItem("companyId", companyId?.toString() ?? "");

      // Arm the service worker to renew the session before the token expires,
      // so a long-lived session never triggers a hard redirect mid-use.
      const expiry = getTokenExpiry(response.data.token);
      if (expiry) scheduleRefresh(expiry);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401 && !ENV.SSO_DISABLED) {
        const authServer = ENV.AUTH_SERVER;
        const clientId = ENV.CLIENT_ID;
        const redirectUri = `${window.location.origin}/v1/PlatformAdmin/Callback`;
        const ssoUrl = `${authServer}/api/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;
        window.location.replace(ssoUrl);
      } else {
        if (error instanceof Error) {
          set({ error: error.message, isLoading: false });
        } else {
          set({ error: String(error), isLoading: false });
        }
      }
    }
  },
  setLoginAnswer: (loginAnswer) => {
    set({ loginAnswer, isAdmin: loginAnswer.captureUser?.role === CAPTURE_ROLES.ADMIN });
  },
  setCurrentCompany: (currentCompany) => {
    set({ currentCompany });
  },
  // Action to refresh login data
  refreshMe: async () => {
    const { loadLoginData } = get();
    await loadLoginData();
  },
  refreshLoginAnswer: async () => {
    const { setLoginAnswer } = get();

    try {
      const { data: loginAnswer } = await userApi.me(apiDefaultParams.apiVersion);
      setLoginAnswer(loginAnswer);
    } catch (error) {
      console.error("Error reloading login answer", error);
    }
  },

  switchCompany: async (companyName, navigate, routerLocation) => {
    set({ isSwitchingCompanyView: true });
    const { data: companyList } = await companyApi.v1.getAllCompaniesNameId(apiDefaultParams.authorization, apiDefaultParams.authVersion, "");

    set({ companyList: companyList ?? [] });

    const companyId = companyList?.find((c) => c.name === companyName)?.id;

    window.localStorage.setItem("companyId", companyId?.toString() ?? "");

    if (routerLocation.pathname.startsWith("/dashboards/")) {
      // A dashboard is company specific, navigate away when switching company
      navigate({ to: "/dashboards" });
    }

    set({ isSwitchingCompanyView: false });
  },
  setCurrentTitle: (title) => {
    set({ currentTitle: title });
  },
}));

registerCompanyViewGetter(() => useAppStore.getState().currentCompany);

// Provide the service-worker integration with the store-backed dependencies it
// needs, without sw-register importing the store (which would be circular).
registerSwCallbacks({
  getCompanyView: () => useAppStore.getState().currentCompany,
  onTokenRefreshed: () => {
    void useAppStore
      .getState()
      .refreshLoginAnswer()
      .then(() => {
        const expiry = getTokenExpiry(useAppStore.getState().loginAnswer?.token);
        if (expiry) scheduleRefresh(expiry);
      });
  },
});
